import React, {
  AnchorHTMLAttributes,
  ComponentProps,
  ComponentPropsWithoutRef,
  ReactNode,
  createContext,
  forwardRef,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router';

import { Tooltip } from '@fly/core';
import classNames from 'classnames';

/**
 * Overlay-link pattern — "id-mapped hoisted anchors".
 *
 * Problem: an interactive card that is itself a link cannot contain other
 * `<a>` elements (nested anchors are invalid HTML and trip React's
 * `validateDOMNesting` warning).
 *
 * Solution:
 * - `FlyOverlayCard` renders the card as ONE real `<Link>` (so the whole card,
 *   including its text, navigates) inside a `position: relative` root, plus a
 *   transparent overlay layer that is a *sibling* of the link (never an
 *   ancestor/descendant of it).
 * - `FlyOverlayLink` renders an `invisible` in-flow *spacer twin* (used only to
 *   reserve layout space and to be measured), and portals its REAL `<a>` into
 *   the overlay layer, absolutely positioned on top of the twin (matched by a
 *   `useId()` id + live measurement). The hoisted anchor renders the *visible*,
 *   interactive content — so it is the element the cursor is actually over.
 *
 * Hover/active: because the visible content lives inside the hoisted anchor (not
 * the hidden twin underneath), native `:hover`/`:active` fire on it directly —
 * no need to forward/replicate state across the portal boundary.
 *
 * Stacking: the card `<Link>` paints first (normal flow); the overlay layer
 * paints above it. The layer is `pointer-events-none`, only the hoisted anchors
 * are `pointer-events-auto`, so clicks on a twin's box hit the hoisted anchor
 * (inner nav) while clicks anywhere else fall through to the card link.
 */
type OverlayNav = {
  /** Internal route (react-router). Provide either `to` or `href`. */
  to?: string;
  /** External URL. Provide either `to` or `href`. */
  href?: string;
  /** Router state forwarded when navigating via `to`. */
  state?: Record<string, unknown>;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: string;
};

type OverlayContextValue = {
  /**
   * The overlay layer element. It is the hoisted anchors' positioned offset
   * parent, so twins are measured relative to it. Exposed as state (not a ref)
   * so consumers re-render — and re-measure — once it is mounted, avoiding the
   * bottom-up ref-attach ordering trap of reading an ancestor ref from a child
   * layout effect.
   */
  layerEl: HTMLDivElement | null;
};

const OverlayContext = createContext<OverlayContextValue | null>(null);

/**
 * `true` when rendered anywhere inside a `FlyOverlayCard`. Lets a shared child
 * that is itself a link (and is reused both inside and outside overlay cards)
 * decide whether to hoist its anchor via `FlyOverlayLink` (avoiding an invalid
 * nested `<a>`) or fall back to a normal in-flow link. Keyed off the provider's
 * presence — not `layerEl` — so it is stable from the first render (the layer
 * mounts a tick later) and never flips after mount.
 */
export function useIsInsideOverlayCard(): boolean {
  return useContext(OverlayContext) != null;
}

export type FlyOverlayCardProps = OverlayNav & {
  /** Accessible name for the card link (it wraps mixed content). */
  'aria-label': string;
  /** Classes for the root `div` (layout, hover background, etc.). */
  className?: string;
  /** Classes for the real card `<Link>` (defaults include `block`). */
  linkClassName?: string;
  /** Test id for the root `div`. */
  'data-testid'?: string;
  /** Test id for the card link. */
  linkTestId?: string;
  children: ReactNode;
} & Omit<
    ComponentPropsWithoutRef<'div'>,
    'className' | 'children' | 'aria-label'
  >;

/**
 * `forwardRef` + root-`div` prop spreading so the card can be used as a Radix
 * `Slot`/`asChild` trigger child (e.g. wrapped in a `ContextMenu`, which clones
 * its single child and forwards `ref` + `onContextMenu` onto it). Anything not
 * consumed here lands on the root `div` (the natural trigger surface).
 */
export const FlyOverlayCard = forwardRef<HTMLDivElement, FlyOverlayCardProps>(
  function FlyOverlayCard(
    {
      to,
      href,
      state,
      target,
      rel,
      'aria-label': ariaLabel,
      className,
      linkClassName,
      'data-testid': dataTestId,
      linkTestId,
      children,
      ...rootProps
    },
    ref
  ) {
    const [layerEl, setLayerEl] = useState<HTMLDivElement | null>(null);

    // `layerEl` is set once (on mount) and never changes, so a stable context
    // value keeps every nested FlyOverlayLink from re-rendering whenever this
    // card re-renders for unrelated reasons.
    const ctxValue = useMemo(() => ({ layerEl }), [layerEl]);

    const linkClass = classNames('block', linkClassName);
    const cardLink =
      href != null ? (
        <a
          href={href}
          target={target}
          rel={rel}
          aria-label={ariaLabel}
          className={linkClass}
          data-testid={linkTestId}
        >
          {children}
        </a>
      ) : (
        <RouterLink
          to={to ?? ''}
          state={state}
          target={target}
          rel={rel}
          aria-label={ariaLabel}
          className={linkClass}
          data-testid={linkTestId}
        >
          {children}
        </RouterLink>
      );

    return (
      <OverlayContext.Provider value={ctxValue}>
        <div
          ref={ref}
          className={classNames('relative', className)}
          data-testid={dataTestId}
          {...rootProps}
        >
          {cardLink}
          {/* Raised above the card link (z-10) so hoisted anchors win the hit
            test. The layer ignores pointer events; only the anchors re-enable
            them, so the rest of the card still falls through to the link. */}
          <div
            ref={setLayerEl}
            className='inset-0 pointer-events-none absolute z-10'
          />
        </div>
      </OverlayContext.Provider>
    );
  }
);

type TwinRect = { left: number; top: number; width: number; height: number };

type HoistedAnchorProps = OverlayNav & {
  /** Shared id linking this anchor to its twin (`data-overlay-id`). */
  overlayId: string;
  /** Measured twin box, layer-relative; `null` until the first measure. */
  rect: TwinRect | null;
  /** Accessible name (the visible content is `aria-hidden`). */
  ariaLabel: string;
  linkClassName?: string;
  'data-testid'?: string;
  tooltip?: ReactNode;
  tooltipProps?: Omit<ComponentProps<typeof Tooltip>, 'children' | 'content'>;
  /**
   * Wrap the VISIBLE hoisted anchor (e.g. in a Popover whose trigger MUST be
   * the visible element). Receives the built anchor; must render it. Takes
   * precedence over `tooltip` when both are supplied.
   */
  wrapAnchor?: (anchor: ReactNode) => ReactNode;
  children: ReactNode;
};

/**
 * The real `<a>`/`<Link>` that is portaled into the overlay layer and pinned
 * over its twin. Carries the VISIBLE, interactive content (so native
 * `:hover`/`:active` fire on it) plus an `sr-only` accessible name.
 */
function HoistedAnchor({
  to,
  href,
  state,
  target,
  rel,
  overlayId,
  rect,
  ariaLabel,
  linkClassName,
  'data-testid': dataTestId,
  tooltip,
  tooltipProps,
  wrapAnchor,
  children,
}: HoistedAnchorProps) {
  const sharedProps = {
    target,
    rel,
    'data-overlay-id': overlayId,
    'data-testid': dataTestId,
    // CRITICAL: a portaled node's React synthetic events bubble through the
    // React tree, not the DOM tree. This anchor is a DOM sibling of the card
    // link, but in the React tree it still sits inside the card link's
    // children — so without this, a left-click would bubble up to the card
    // `<Link>` and trigger its navigation (the external `href` only "wins" on
    // right-click / middle-click, which don't run React click handlers).
    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation(),
    className: classNames(
      'pointer-events-auto absolute inline-flex items-center',
      linkClassName
    ),
    // `pointerEvents` is set inline (not only via class) so the anchor
    // reliably overrides the layer's `pointer-events: none`, regardless of
    // utility-class generation/order. Hidden until measured, then pinned
    // over the twin's box.
    style: rect
      ? {
          pointerEvents: 'auto' as const,
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        }
      : { pointerEvents: 'none' as const, opacity: 0 },
  };
  // The content is `aria-hidden` (the twin already represents it visually and
  // is itself hidden); the accessible name comes from the sr-only label, which
  // also satisfies jsx-a11y/anchor-has-content.
  const anchorContent = (
    <>
      <span aria-hidden className='contents'>
        {children}
      </span>
      <span className='sr-only'>{ariaLabel}</span>
    </>
  );
  const anchor =
    href != null ? (
      <a href={href} {...sharedProps}>
        {anchorContent}
      </a>
    ) : (
      <RouterLink to={to ?? ''} state={state} {...sharedProps}>
        {anchorContent}
      </RouterLink>
    );

  if (wrapAnchor) return <>{wrapAnchor(anchor)}</>;

  return tooltip != null ? (
    <Tooltip {...tooltipProps} content={tooltip}>
      {anchor}
    </Tooltip>
  ) : (
    anchor
  );
}

export type FlyOverlayLinkProps = OverlayNav & {
  /** Accessible name (the twin is `aria-hidden`). */
  'aria-label': string;
  /** Classes for the in-place twin wrapper. */
  className?: string;
  /** Extra classes for the hoisted anchor. */
  linkClassName?: string;
  /** Test id for the hoisted anchor (the interactive element). */
  'data-testid'?: string;
  /** Optional tooltip rendered on the hoisted anchor (hover lives there). */
  tooltip?: ReactNode;
  /** Extra props for the tooltip (side, backgroundColor, delayDuration, …). */
  tooltipProps?: Omit<ComponentProps<typeof Tooltip>, 'children' | 'content'>;
  /**
   * Wrap the VISIBLE hoisted anchor (e.g. in a Popover whose trigger MUST be
   * the visible element, not the invisible twin). Receives the built anchor and
   * must render it. Takes precedence over `tooltip`.
   */
  wrapAnchor?: (anchor: ReactNode) => ReactNode;
  /** The look-alike visual (e.g. a `Button` with no `to`/`href`). */
  children: ReactNode;
};

export function FlyOverlayLink({
  to,
  href,
  state,
  target,
  rel,
  'aria-label': ariaLabel,
  className,
  linkClassName,
  'data-testid': dataTestId,
  tooltip,
  tooltipProps,
  wrapAnchor,
  children,
}: FlyOverlayLinkProps) {
  const id = useId();
  const layerEl = useContext(OverlayContext)?.layerEl ?? null;
  const twinRef = useRef<HTMLSpanElement>(null);
  const [rect, setRect] = useState<TwinRect | null>(null);

  useLayoutEffect(() => {
    const twin = twinRef.current;
    if (!twin || !layerEl) return;

    const measure = () => {
      const twinBox = twin.getBoundingClientRect();
      // Position relative to the layer: it is the anchor's offset parent, so
      // this also accounts for any padding/border on the card root.
      const layerBox = layerEl.getBoundingClientRect();
      const next: TwinRect = {
        left: twinBox.left - layerBox.left,
        top: twinBox.top - layerBox.top,
        width: twinBox.width,
        height: twinBox.height,
      };
      // Bail out when the box is unchanged. The observer also watches the layer
      // and the row, which fire on changes that don't move *this* twin — without
      // this guard each such fire would re-render and rewrite identical styles.
      setRect((prev) =>
        prev &&
        prev.left === next.left &&
        prev.top === next.top &&
        prev.width === next.width &&
        prev.height === next.height
          ? prev
          : next
      );
    };
    measure();

    // A single ResizeObserver covers every reposition trigger, so no separate
    // window 'resize' listener is needed (the layer is `inset-0` of the card
    // root, so viewport/card changes resize it and fire here):
    //  - twin: the anchor's own box changed.
    //  - layer: the card region (offset parent) changed — incl. viewport resize.
    //  - row (twin's flow parent): a *sibling* in the same row changed size
    //    (async-loaded badges, variable-width text) and shifted the twin even
    //    though the twin itself didn't resize.
    const observer = new ResizeObserver(measure);
    observer.observe(twin);
    observer.observe(layerEl);
    const row = twin.parentElement;
    if (row) observer.observe(row);

    let cancelled = false;
    void document.fonts?.ready?.then(() => {
      if (!cancelled) measure();
    });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [layerEl]);

  const hoistedAnchor = layerEl
    ? createPortal(
        <HoistedAnchor
          to={to}
          href={href}
          state={state}
          target={target}
          rel={rel}
          overlayId={id}
          rect={rect}
          ariaLabel={ariaLabel}
          linkClassName={linkClassName}
          data-testid={dataTestId}
          tooltip={tooltip}
          tooltipProps={tooltipProps}
          wrapAnchor={wrapAnchor}
        >
          {children}
        </HoistedAnchor>,
        layerEl
      )
    : null;

  return (
    <span
      ref={twinRef}
      data-twin-id={id}
      aria-hidden
      // `invisible` (visibility:hidden) keeps the layout box for measurement
      // while hiding the duplicate paint — only the hoisted anchor's copy shows.
      className={classNames(
        'pointer-events-none invisible inline-flex',
        className
      )}
    >
      {children}
      {hoistedAnchor}
    </span>
  );
}
